/**
 * SimpleSlider - A customizable slider implementation with responsive functionality
 * @param {Object} options - Configuration options
 * @param {string} options.sliderSelector - CSS selector for the slider container
 * @param {string} options.slideSelector - CSS selector for individual slides
 * @param {string|Element} options.nextBtn - Next button selector or Element (optional)
 * @param {string|Element} options.prevBtn - Previous button selector or Element (optional)
 * @param {number} options.initialSlide - Initial slide index (0-based, default: 0)
 * @param {number} options.animationDuration - Transition duration in ms (default: 300)
 * @param {number} options.slidesToShow - Number of slides to show at once (default: 1)
 * @param {boolean} options.infinite - Whether to loop infinitely (default: true)
 * @param {boolean} options.autoplay - Enable auto sliding (default: false)
 * @param {number} options.autoplaySpeed - Time between auto slides in ms (default: 3000)
 * @param {boolean} options.pauseOnHover - Pause autoplay on hover (default: true)
 * @param {boolean} options.peek - Show a peek of the next slide (default: false)
 * @param {string} options.effect - Transition effect: 'slide' or 'fade' (default: 'slide')
 * @param {Object} options.syncWith - Another slider instance to sync with (optional)
 * @param {Object[]} options.responsive - Breakpoints for responsive behavior
 * @param {Function} options.beforeSlide - Callback before slide changes
 * @param {Function} options.afterSlide - Callback after slide changes
 */
class SimpleSlider {
  constructor(options) {
    // Default options
    this.options = {
      sliderSelector: '.slider',
      slideSelector: '.slide',
      nextBtn: null,
      prevBtn: null,
      initialSlide: 0,
      animationDuration: 300,
      slidesToShow: 1,
      slidesToScroll: 1,
      infinite: true,
      autoplay: false,
      autoplaySpeed: 3000,
      pauseOnHover: true,
      peek: false,
      effect: 'slide', // 'slide' or 'fade'
      syncWith: null,  // Another slider instance to sync with
      responsive: [],
      beforeSlide: null,
      afterSlide: null,
      ...options
    };

    // Find DOM elements
    this.slider = typeof this.options.sliderSelector === 'string' 
      ? document.querySelector(this.options.sliderSelector) 
      : this.options.sliderSelector;
      
    if (!this.slider) {
      console.error(`Slider container not found: ${this.options.sliderSelector}`);
      return;
    }

    // Find original slides and store their properties
    this.originalSlides = this.slider.querySelectorAll(this.options.slideSelector);
    if (!this.originalSlides.length) {
      console.error(`No slides found using selector: ${this.options.slideSelector}`);
      return;
    }
    
    // Store original dimensions and styles
    this.originalStyles = {
      slider: {
        width: this.slider.style.width || '',
        height: this.slider.style.height || '',
        position: this.slider.style.position || '',
        overflow: this.slider.style.overflow || ''
      },
      slides: Array.from(this.originalSlides).map(slide => ({
        width: slide.style.width || '',
        height: slide.style.height || '',
        display: slide.style.display || '',
        margin: slide.style.margin || '',
        padding: slide.style.padding || ''
      }))
    };
    
    // Create slider structure
    this.setupSliderDOM();
    
    // Now query the newly created structure
    this.sliderTrack = this.slider.querySelector('.slider-track');
    this.slides = this.sliderTrack.querySelectorAll(this.options.slideSelector);
    
    // Set up next/prev buttons
    this.setupNavButtons();
    
    // State
    this.currentIndex = this.options.initialSlide;
    this.isAnimating = false;
    this.totalSlides = this.slides.length;
    this.currentBreakpoint = null;
    this.slideWidth = 0;
    this.peekWidth = 0;
    this.slidesToShow = this.options.slidesToShow;
    this.slidesToScroll = this.options.slidesToScroll;
    this.autoplayTimer = null;
    this.isPaused = false;
    
    // Initialize
    this.init();
  }
  
  /**
   * Set up the slider DOM structure without affecting original content
   */
  setupSliderDOM() {
    // Store original content
    const originalHTML = this.slider.innerHTML;
    
    // Create track element
    const track = document.createElement('div');
    track.className = 'slider-track';
    
    // Add original slides to track
    track.innerHTML = originalHTML;
    
    // Clear slider and append track
    this.slider.innerHTML = '';
    this.slider.appendChild(track);
  }
  
  /**
   * Set up navigation buttons
   */
  setupNavButtons() {
      // Next button
      if (this.options.nextBtn) {
        if (typeof this.options.nextBtn === 'string') {
          this.nextBtns = document.querySelectorAll(this.options.nextBtn);
        } else {
          this.nextBtns = [this.options.nextBtn];
        }
      }
      
      // Prev button
      if (this.options.prevBtn) {
        if (typeof this.options.prevBtn === 'string') {
          this.prevBtns = document.querySelectorAll(this.options.prevBtn);
        } else {
          this.prevBtns = [this.options.prevBtn];
        }
      }
  }

  /**
   * Initialize the slider with enhanced resize handling
   */
  init() {
    // Apply initial styling
    this.setupSliderStyles();
    
    // Calculate dimensions
    this.calculateDimensions();
    
    // Set initial position
    this.goToSlide(this.currentIndex, false);
    
    // Add event listeners to buttons
    if (this.nextBtns) {
        this.nextBtns.forEach(btn => {
          btn.addEventListener('click', this.handleNextClick.bind(this));
        });
      }
      
      if (this.prevBtns) {
        this.prevBtns.forEach(btn => {
          btn.addEventListener('click', this.handlePrevClick.bind(this));
        });
      }
    
    // Set up hover pause functionality
    if (this.options.autoplay && this.options.pauseOnHover) {
      // Use bound methods to ensure correct context
      this.boundPause = this.pause.bind(this);
      this.boundHandleMouseLeave = this.handleMouseLeave.bind(this);
      
      this.slider.addEventListener('mouseenter', this.boundPause);
      this.slider.addEventListener('mouseleave', this.boundHandleMouseLeave);
    }
    
    // Add resize handler regardless of responsive setting
    // This ensures proper rendering when container size changes
    window.addEventListener('resize', this.debouncedResize.bind(this));
    
    // Handle container size changes using ResizeObserver if available
    if (typeof ResizeObserver !== 'undefined') {
      this.containerObserver = new ResizeObserver(entries => {
        // Only recalculate if the container size actually changed
        if (entries && entries[0] && entries[0].target === this.slider) {
          this.calculateDimensions();
          this.goToSlide(this.currentIndex, false);
        }
      });
      this.containerObserver.observe(this.slider);
    }
    
    // Start autoplay if enabled
    if (this.options.autoplay) {
      this.play();
    }
  }
  
  /**
   * Event handler for next button click
   */
  handleNextClick() {
    this.next();
    // Pause autoplay if manually navigating
    if (this.options.autoplay) {
      this.pause();
      // Restart after a brief pause to avoid immediate advance
      setTimeout(() => {
        if (!this.isPaused) this.play();
      }, this.options.autoplaySpeed / 2);
    }
  }
  
  /**
   * Event handler for prev button click
   */
  handlePrevClick() {
    this.prev();
    // Pause autoplay if manually navigating
    if (this.options.autoplay) {
      this.pause();
      // Restart after a brief pause to avoid immediate advance
      setTimeout(() => {
        if (!this.isPaused) this.play();
      }, this.options.autoplaySpeed / 2);
    }
  }
  
  /**
   * Event handler for mouse leave
   */
  handleMouseLeave() {
    if (!this.isPaused) this.play();
  }

  /**
   * Set up slider styling for CSS-based transitions
   * Preserves original dimensions and aspect ratios
   */
  setupSliderStyles() {
    // Make sure the slider has relative positioning
    if (getComputedStyle(this.slider).position === 'static') {
      this.slider.style.position = 'relative';
    }
    
    // Make sure the slider has overflow hidden
    this.slider.style.overflow = 'hidden';
    
    // Compute gap from CSS if specified
    const computedStyle = window.getComputedStyle(this.slider);
    this.gap = parseFloat(computedStyle.gap) || parseFloat(computedStyle.columnGap) || 0;
    this.hasGap = this.gap > 0;

    // Set up the track for horizontal sliding
    this.sliderTrack.style.display = 'flex';
    this.sliderTrack.style.willChange = 'transform'; // Optimize for animation performance
    
    // Different setup based on transition effect
    if (this.options.effect === 'fade') {
      // For fade effect, modify track and slides differently
      this.sliderTrack.style.flexWrap = 'nowrap';
      this.sliderTrack.style.position = 'relative';
      this.sliderTrack.style.width = '100%';
      this.sliderTrack.style.height = '100%';
      this.sliderTrack.style.transform = 'none'; // No sliding transform
    } else {
      // For slide effect, use transform transition
      this.sliderTrack.style.transition = `transform ${this.options.animationDuration}ms ease`;
      this.sliderTrack.style.alignItems = 'stretch'; // Full height for all slides
    }
    
    // Copy gap property if it exists and using slide effect
    if (this.hasGap && this.options.effect === 'slide') {
      this.sliderTrack.style.gap = `${this.gap}px`;
      this.sliderTrack.style.columnGap = `${this.gap}px`;
    } else {
      // Fix for left margin issue - ensure no default margins/paddings when no gap
      this.sliderTrack.style.margin = '0';
      this.sliderTrack.style.padding = '0';
    }
    
    // Get original slide dimensions to reference later
    this.originalSlideDimensions = Array.from(this.slides).map(slide => {
      const style = window.getComputedStyle(slide);
      return {
        width: parseFloat(style.width),
        height: parseFloat(style.height),
        aspectRatio: style.aspectRatio !== 'auto' ? style.aspectRatio : null
      };
    });
    
    // Set up slides differently based on transition effect
    Array.from(this.slides).forEach((slide, index) => {
      const computedStyle = window.getComputedStyle(slide);
      
      // Common styles for all slides
      slide.style.boxSizing = 'border-box';
      
      if (this.options.effect === 'fade') {
        // For fade transition
        slide.style.opacity = index === this.options.initialSlide ? '1' : '0';
        slide.style.transition = `opacity ${this.options.animationDuration}ms ease`;
        slide.style.position = 'absolute';
        slide.style.top = '0';
        slide.style.left = '0';
        slide.style.width = '100%';
        slide.style.height = '100%';
        slide.style.zIndex = index === this.options.initialSlide ? '1' : '0';
      } else {
        // For slide transition
        // slide.style.flex = '0 0 auto'; // Don't grow or shrink
        slide.style.width = `${computedStyle.width}px`;
        // If using aspect-ratio, ensure it's maintained
        if (computedStyle.aspectRatio !== 'auto') {
          slide.style.aspectRatio = computedStyle.aspectRatio;
        } 
        
        // Don't override margins if gap is used
        if (!this.hasGap) {
          slide.style.margin = '0';
        }
      }
      // 
      slide.setAttribute("data-index",index);
      // Preserve original padding
      slide.style.padding = computedStyle.padding; 
    });
    
    // Apply peek styling if enabled and using slide effect
    if (this.options.peek && this.options.effect === 'slide') {
      this.slider.classList.add('peek-enabled');
    } else {
      this.slider.classList.remove('peek-enabled');
    }
    
    // Apply transition effect class
    this.slider.classList.add(`effect-${this.options.effect}`);
  }

  /**
   * Calculate dimensions of slider and slides
   * Preserves aspect ratio and original dimensions
   */
  calculateDimensions() {
    // For fade effect, dimensions are handled differently
    if (this.options.effect === 'fade') {
      // In fade mode, we don't need to calculate slide widths the same way
      // Just ensure all slides are stacked correctly and container is sized properly
      
      // Set container dimensions based on largest slide
      let maxWidth = 0;
      let maxHeight = 0;
      
      Array.from(this.slides).forEach(slide => {
        const computedStyle = window.getComputedStyle(slide);
        const width = parseFloat(computedStyle.width);
        const height = parseFloat(computedStyle.height);
        
        if (width > maxWidth) maxWidth = width;
        if (height > maxHeight) maxHeight = height;
      });
      
      // Set minimum size for the slider container
      if (maxWidth > 0 && maxHeight > 0) {
        this.slider.style.minWidth = `${maxWidth}px`;
        this.slider.style.minHeight = `${maxHeight}px`;
      }
      
      return; // No need for further calculations for fade effect
    }
    
    // Get slider width
    const sliderWidth = this.slider.offsetWidth;
    
    // Get original slide dimensions
    const originalSlideStyles = Array.from(this.slides).map(slide => {
      const style = window.getComputedStyle(slide);
      return {
        width: parseFloat(style.width),
        height: parseFloat(style.height),
        aspectRatio: style.aspectRatio
      };
    });
    
    // Measure standard slide width based on the first slide
    const originalWidth = originalSlideStyles[0].width;
    const hasOriginalWidth = !isNaN(originalWidth) && originalWidth > 0;
    
    // Always use the original width for slides when available
    if (hasOriginalWidth) {
      this.slideWidth = originalWidth;
    } else {
      // Fallback calculation if no width is defined
      const availableWidth = sliderWidth - (this.hasGap ? (this.slidesToShow - 1) * this.gap : 0);
      this.slideWidth = availableWidth / this.slidesToShow;
    }

    console.log(this.options.sliderSelector + "Slide Width"+originalWidth)
    // In all cases, maintain original dimensions and aspect ratio
    Array.from(this.slides).forEach(slide => {
      // Don't alter width - this preserves aspect ratio
      // Only set these properties:
      slide.style.boxSizing = 'border-box';
      slide.style.overflow = 'hidden';
      
      // Handle images with aspect-ratio
      const images = slide.querySelectorAll('img');
      images.forEach(img => {
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
        img.style.display = 'block';
      });
    });
    
    // Setup peek - does not change slide dimensions, just affects visibility
    if (this.options.peek) {
      this.slider.classList.add('peek-enabled');
    } else {
      this.slider.classList.remove('peek-enabled');
    }
    
    // Calculate total track width based on actual slide widths and gaps
    const totalTrackWidth = Array.from(this.slides).reduce((total, slide) => {
      return total + parseFloat(window.getComputedStyle(slide).width);
    }, 0) + (this.hasGap ? (this.totalSlides - 1) * this.gap : 0);
    
    // Set track width with extra buffer
    this.sliderTrack.style.width = `${totalTrackWidth + 20}px`;
  }

  /**
   * Enhanced debounced resize handler with immediate initial calculation
   * to avoid UI jumps during resize
   */
  debouncedResize() {
    // Perform immediate calculation to prevent jumpy UI during resize
    this.checkResponsive();
    this.calculateDimensions();
    this.goToSlide(this.currentIndex, false);
    
    // Then debounce for full recalculation
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    
    this.resizeTimer = setTimeout(() => {
      this.checkResponsive();
      this.calculateDimensions();
      this.goToSlide(this.currentIndex, false);
    }, 100); // Reduced from 250ms for faster response
  }

  /**
   * Check current viewport and adjust settings based on responsive breakpoints
   */
  checkResponsive() {
    const windowWidth = window.innerWidth;
    let matchedBreakpoint = null;
    
    // Find the appropriate breakpoint
    if (this.options.responsive) {
      // Sort breakpoints in descending order
      const sortedBreakpoints = [...this.options.responsive].sort((a, b) => b.breakpoint - a.breakpoint);
      
      // Find the first matching breakpoint
      for (const bp of sortedBreakpoints) {
        if (windowWidth <= bp.breakpoint) {
          matchedBreakpoint = bp;
        }
      }
    }
    
    // If we matched a breakpoint and it's different from current
    if (matchedBreakpoint && matchedBreakpoint !== this.currentBreakpoint) {
      this.currentBreakpoint = matchedBreakpoint;
      
      // Apply new settings
      if (matchedBreakpoint.settings.slidesToShow) {
        this.slidesToShow = matchedBreakpoint.settings.slidesToShow;
      }
      
      if (matchedBreakpoint.settings.slidesToScroll) {
        this.slidesToScroll = matchedBreakpoint.settings.slidesToScroll;
      }
      
      // Handle responsive peek settings
      if (matchedBreakpoint.settings.peek !== undefined) {
        this.options.peek = matchedBreakpoint.settings.peek;
      }
      
      // Handle effect settings if specified
      if (matchedBreakpoint.settings.effect !== undefined) {
        this.options.effect = matchedBreakpoint.settings.effect;
        // Re-setup styles for new effect
        this.setupSliderStyles();
      }
    } 
    // If we didn't match any breakpoint, revert to defaults
    else if (!matchedBreakpoint && this.currentBreakpoint) {
      this.currentBreakpoint = null;
      this.slidesToShow = this.options.slidesToShow;
      this.slidesToScroll = this.options.slidesToScroll;
      this.options.peek = this.options.peek;
      this.options.effect = this.options.effect;
    }
  }

  /**
   * Go to the next slide
   */
  next() {
    let nextIndex;
    
    if (this.options.infinite) {
      nextIndex = (this.currentIndex + this.slidesToScroll) % this.totalSlides;
    } else {
      nextIndex = Math.min(this.currentIndex + this.slidesToScroll, this.totalSlides - this.slidesToShow);
      
      // Disable next button if at the end
      if (this.nextBtn && nextIndex === this.currentIndex) {
        this.nextBtn.disabled = true;
      }
      
      // Re-enable prev button
      if (this.prevBtn) {
        this.prevBtn.disabled = false;
      }
    }
    
    this.goToSlide(nextIndex);
  }

  /**
   * Go to the previous slide
   */
  prev() {
    let prevIndex;
    
    if (this.options.infinite) {
      prevIndex = (this.currentIndex - this.slidesToScroll + this.totalSlides) % this.totalSlides;
    } else {
      prevIndex = Math.max(this.currentIndex - this.slidesToScroll, 0);
      
      // Disable prev button if at the beginning
      if (this.prevBtn && prevIndex === this.currentIndex) {
        this.prevBtn.disabled = true;
      }
      
      // Re-enable next button
      if (this.nextBtn) {
        this.nextBtn.disabled = false;
      }
    }
    
    this.goToSlide(prevIndex);
  }

  /**
   * Go to a specific slide with improved positioning for aspect-ratio slides
   * Supports both slide and fade effects
   * @param {number} index - The index of the slide to go to
   * @param {boolean} animate - Whether to animate the transition
   * @param {boolean} bySync - Whether this was triggered by sync with another slider
   */
  goToSlide(index, animate = true, bySync = false) {
    // Prevent rapid clicking
    if (this.isAnimating) return;
    
    // Validate index
    if (index < 0 || index >= this.totalSlides) {
      console.error(`Invalid slide index: ${index}`);
      return;
    }

    // Don't do anything if we're already on this slide
    if (index === this.currentIndex && animate) return;

    // Mark as animating if animating
    this.isAnimating = animate;
    
    // Call beforeSlide callback
    if (typeof this.options.beforeSlide === 'function') {
      this.options.beforeSlide({
        currentIndex: this.currentIndex,
        nextIndex: index,
        totalSlides: this.totalSlides,
        slidesToShow: this.slidesToShow
      });
    }

    // Handle transition effect
    if (this.options.effect === 'fade') {
      // Fade transition
      Array.from(this.slides).forEach((slide, i) => {
        if (i === index) {
          // Next slide becomes visible
          slide.style.opacity = '1';
          slide.style.zIndex = '1';
        } else {
          // Other slides become invisible
          slide.style.opacity = '0';
          slide.style.zIndex = '0';
        }
      });
    } else {
      // Slide transition
      // Calculate position
      let position = 0;
      
      // Calculate exact position by summing widths
      for (let i = 0; i < index; i++) {
        const slideWidth = parseFloat(window.getComputedStyle(this.slides[i]).width);
        position += slideWidth;
        
        // Add gap if present and not the last slide
        if (this.hasGap && i < this.totalSlides - 1) {
          position += this.gap;
        }
      }
      position = -1 * position;
      
      // Apply transform with or without transition
      if (!animate) {
        this.sliderTrack.style.transition = 'none';
      } else {
        this.sliderTrack.style.transition = `transform ${this.options.animationDuration}ms ease`;
      }
      
      this.sliderTrack.style.transform = `translateX(${position}px)`;
    }
    
    // Update slide visibility classes
    const activeClass = 'slider-active';
    const peekClass = 'slider-peek';
    
    Array.from(this.slides).forEach((slide, i) => {
      // Reset classes
      slide.classList.remove(activeClass, peekClass);
      
      // Set active class on current slide
      if (i === index) {
        slide.classList.add(activeClass);
      } 
      // Set peek class on next slide when peek is enabled
      else if (this.options.peek && this.options.effect === 'slide' && i === index + 1) {
        slide.classList.add(peekClass);
      }
    });
    
    // Update current index
    this.currentIndex = index;
    
    // Reset transition after non-animated move for slide effect
    if (!animate && this.options.effect === 'slide') {
      setTimeout(() => {
        this.sliderTrack.style.transition = `transform ${this.options.animationDuration}ms ease`;
      }, 0);
    }
    
    // If we're synced with another slider and this wasn't triggered by sync
    if (this.options.syncWith && !bySync) {
      // Sync the other slider with this one
      this.options.syncWith.goToSlide(index, animate, true);
    }
    
    // After animation completes
    if (animate) {
      setTimeout(() => {
        this.isAnimating = false;
        
        // Call afterSlide callback
        if (typeof this.options.afterSlide === 'function') {
          this.options.afterSlide({
            currentIndex: this.currentIndex,
            totalSlides: this.totalSlides,
            slidesToShow: this.slidesToShow
          });
        }
      }, this.options.animationDuration);
    } else {
      this.isAnimating = false;
      
      // Call afterSlide callback immediately if not animating
      if (typeof this.options.afterSlide === 'function') {
        this.options.afterSlide({
          currentIndex: this.currentIndex,
          totalSlides: this.totalSlides,
          slidesToShow: this.slidesToShow
        });
      }
    }
  }

  /**
   * Start autoplay
   */
  play() {
    // Clear any existing timer
    this.pause();
    
    // Start a new timer
    this.autoplayTimer = setInterval(() => {
      this.next();
    }, this.options.autoplaySpeed);
    
    this.isPaused = false;
    
    // Also play synced slider if it exists
    if (this.options.syncWith && this.options.syncWith.options.autoplay && this.options.syncWith.isPaused) {
      this.options.syncWith.play();
    }
  }
  
  /**
   * Pause autoplay
   */
  pause() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
  
  /**
   * Toggle autoplay
   * @returns {boolean} New autoplay state
   */
  toggleAutoplay() {
    if (this.autoplayTimer) {
      this.pause();
      this.isPaused = true;
    } else {
      this.play();
      this.isPaused = false;
    }
    
    return !this.isPaused;
  }

  /**
   * Get the current slide index (0-based)
   * @returns {number} The current slide index
   */
  getCurrentSlide() {
    return this.currentIndex;
  }
  
  /**
   * Get the current slide number (1-based, for display purposes)
   * @returns {number} The current slide number
   */
  getCurrentSlideNumber() {
    return this.currentIndex + 1;
  }
  
  /**
   * Get the total number of slides
   * @returns {number} The total number of slides
   */
  getTotalSlides() {
    return this.totalSlides;
  }
  
  /**
   * Get the current number of slides to show
   * @returns {number} The current number of slides to show
   */
  getSlidesToShow() {
    return this.slidesToShow;
  }
  
  /**
   * Manually refresh the slider (recalculate dimensions, etc.)
   * Useful after DOM changes
   */
  refresh() {
    this.checkResponsive();
    this.calculateDimensions();
    this.goToSlide(this.currentIndex, false);
  }
  
  /**
   * Synchronize with another slider
   * @param {SimpleSlider} slider - Another slider instance to sync with
   * @returns {SimpleSlider} - Returns this instance for chaining
   */
  sync(slider) {
    if (slider && typeof slider === 'object') {
      this.options.syncWith = slider;
      slider.options.syncWith = this;
    }
    return this;
  }
  
  /**
   * Destroy the slider and clean up
   */
  destroy() {
    // Stop autoplay
    this.pause();
    
    // Stop observing resize if active
    if (this.containerObserver) {
      this.containerObserver.disconnect();
    }
    
    // Remove event listeners
    if (this.nextBtn) {
      this.nextBtn.removeEventListener('click', this.handleNextClick);
    }
    
    if (this.prevBtn) {
      this.prevBtn.removeEventListener('click', this.handlePrevClick);
    }
    
    if (this.options.pauseOnHover) {
      this.slider.removeEventListener('mouseenter', this.boundPause);
      this.slider.removeEventListener('mouseleave', this.boundHandleMouseLeave);
    }
    
    window.removeEventListener('resize', this.debouncedResize);
    
    // Remove sync reference
    if (this.options.syncWith) {
      this.options.syncWith.options.syncWith = null;
      this.options.syncWith = null;
    }
    
    // Remove added classes
    this.slider.classList.remove('peek-enabled', 'effect-slide', 'effect-fade');
    
    // Restore original content and styles
    this.slider.innerHTML = '';
    
    // Different restoration based on effect mode
    if (this.options.effect === 'fade') {
      // For fade effect, restore slides normally
      Array.from(this.slides).forEach(slide => {
        slide.style = '';
        this.slider.appendChild(slide.cloneNode(true));
      });
    } else {
      // For slide effect
      Array.from(this.slides).forEach(slide => {
        this.slider.appendChild(slide.cloneNode(true));
      });
    }
    
    // Restore original styles
    Object.assign(this.slider.style, this.originalStyles.slider);
    
    // Reset slide styles
    const restoreSlides = this.slider.querySelectorAll(this.options.slideSelector);
    Array.from(restoreSlides).forEach((slide, i) => {
      if (this.originalStyles.slides[i]) {
        Object.assign(slide.style, this.originalStyles.slides[i]);
      }
    });
  }
}
